/**
 * The compare-mode baseline: the model exactly as it was loaded, kept for the
 * lifetime of the design so Compare always shows the untouched original.
 *
 * Why this exists: ThreeScene rebuilds `unmodifiedMesh` from whatever model the
 * phased loader just installed. On a checkpoint load (resume / "Previous" via
 * the replay engine's server anchor) that model is the socket *after* earlier
 * steps, so the baseline silently became an already-cut mesh and Compare showed
 * a trimmed "original". The snapshot is captured once on a genuine model load
 * and re-used across every later checkpoint swap.
 *
 * Frame-changing operations (axes alignment bake/undo, locking's world-baked
 * CSG reframe) legitimately move the baseline geometry so it stays registered
 * with the working socket — those call `syncCompareBaseline` to fold the change
 * into the snapshot. Without that the snapshot would drift back to the
 * pre-alignment pose and reinstate an unrotated overlay on the next reload.
 *
 * Heavy geometry lives in a module ref (not Zustand) to keep GPU buffers out of
 * devtools / persist — mirroring `meshSnapshot.js` and `baseMeshSnapshot.js`.
 */
import * as THREE from 'three';

/** @type {{ geometry: import('three').BufferGeometry, position: number[], quaternion: number[], scale: number[] } | null} */
let snapshot = null;

function storeFrom(mesh) {
  snapshot = {
    geometry: mesh.geometry.clone(),
    position: mesh.position.toArray(),
    quaternion: mesh.quaternion.toArray(),
    scale: mesh.scale.toArray(),
  };
}

/**
 * Capture the freshly loaded model as the compare baseline, replacing any
 * previous one. Call only for genuine model loads (upload / raw scan), never
 * for a checkpoint swap.
 * @param {import('three').Mesh} mesh
 * @returns {boolean}
 */
export function captureCompareBaseline(mesh) {
  if (!mesh?.geometry?.attributes?.position?.count) return false;
  snapshot?.geometry?.dispose?.();
  storeFrom(mesh);
  return true;
}

/**
 * Fold a transform that was just baked into the live overlay back into the
 * snapshot, so the stored original keeps the alignment rotation / reframe that
 * registers it against the working socket.
 * @param {import('three').Mesh | null | undefined} unmodifiedMesh
 * @returns {boolean}
 */
export function syncCompareBaseline(unmodifiedMesh) {
  if (!snapshot) return false;
  if (!unmodifiedMesh?.geometry?.attributes?.position?.count) return false;
  snapshot.geometry.dispose?.();
  storeFrom(unmodifiedMesh);
  return true;
}

export function hasCompareBaseline() {
  return snapshot != null;
}

/**
 * Build a fresh overlay mesh from the snapshot (caller owns disposal and is
 * responsible for material styling / render order).
 * @param {import('three').Material} material
 * @returns {import('three').Mesh | null}
 */
export function instantiateCompareBaselineMesh(material) {
  if (!snapshot) return null;
  const mesh = new THREE.Mesh(snapshot.geometry.clone(), material);
  mesh.position.fromArray(snapshot.position);
  mesh.quaternion.fromArray(snapshot.quaternion);
  mesh.scale.fromArray(snapshot.scale);
  mesh.matrixAutoUpdate = true;
  mesh.updateMatrixWorld(true);
  return mesh;
}

export function clearCompareBaseline() {
  snapshot?.geometry?.dispose?.();
  snapshot = null;
}
